import { EllipsisVerticalIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
  ColumnDef,
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { TableSkeleton } from '@/Pages/Home/ResourceTable/TableSkeleton';
import { DataGridColumnVisibilityPopover } from '@/components/DataGrid/DataGridColumnVisibilityPopover';
import { DataGridSearchInput } from '@/components/DataGrid/DataGridSearchInput';
import {
  GridPaginationModel,
  useDataGridState,
} from '@/hooks/useDataGridState';
import { useDebouncedSearchStringQueryParams } from '@/hooks/useDebouncedSearchStringQueryParams';
import { usePermissions } from '@/hooks/usePermissions';
import { useSortingState } from '@/hooks/useSortingState';
import { useStoredResourceTags } from '@/hooks/useStoredResourceTags/useStoredResourceTags';
import { useDatasets, useOrganizationId } from '@/hooks/useSwr';
import { DatasetSchema } from '@/types/schema';
import {
  apiDatasetsPath,
  appDatasetsPath,
  appOrganizationPath,
} from '@/utils/constants';

import { DataGrid, DataGridPagination } from '../DataGrid';
import {
  emulateNativeClick,
  emulateNativeMiddleClick,
  useDataGridSizingLocalStorage,
} from '../DataGrid.utils';
import { DeleteModal } from '../Delete';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../Dropdown';
import { ErrorBanner } from '../ErrorBanner';
import { TableCellTextOverflow } from '../Table';
import { TextOverflowTooltip } from '../TextOverflowTooltip';

type SimpleTableProps =
  | {
      simple: true;
      defaultPaginationModel: GridPaginationModel;
    }
  | {
      simple?: false;
    };

type TableProps = {
  emptyState?: React.ReactNode;
} & SimpleTableProps;

const columnHelper = createColumnHelper<DatasetSchema>();

const formatter = new Intl.NumberFormat('en-US', {});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const COLUMNS_DEF: ColumnDef<DatasetSchema, any>[] = [
  columnHelper.accessor('name', {
    header: 'Dataset',
    enableSorting: true,
    cell: ({ row }) => {
      const description = row.original.description;
      const descriptionClass = 'max-h-24 overflow-y-auto text-sm text-gray-500';

      const tooltip = (
        <div>
          <div>{row.original.name}</div>
          {description && (
            <div className={descriptionClass}>{description.trim()}</div>
          )}
        </div>
      );

      return (
        <div>
          <TextOverflowTooltip customTooltip={tooltip} align="start">
            {row.original.name}
          </TextOverflowTooltip>
          {description && (
            <TextOverflowTooltip
              className={descriptionClass}
              customTooltip={tooltip}
              align="start"
            >
              {description.trim()}
            </TextOverflowTooltip>
          )}
        </div>
      );
    },
  }),
  columnHelper.accessor('session_count', {
    header: 'Experiments',
    enableSorting: true,
    cell: ({ getValue }) => (
      <>{getValue() != null && formatter.format(getValue())}</>
    ),
  }),
  columnHelper.accessor('last_session_start_time', {
    header: 'Last Experiment',
    cell: ({ row }) =>
      row.original.last_session_start_time
        ? new Date(row.original.last_session_start_time + 'Z').toLocaleString()
        : undefined,
    enableSorting: true,
    size: 200,
  }),
  columnHelper.accessor('example_count', {
    header: 'Examples',
    enableSorting: true,
    cell: ({ getValue }) => (
      <>{getValue() != null && formatter.format(getValue())}</>
    ),
  }),
  columnHelper.accessor('data_type', {
    id: 'data_type',
    header: 'Type',
    enableSorting: false,
    enableHiding: true,
  }),
  columnHelper.accessor('created_at', {
    header: 'Created At',
    cell: ({ row }) => new Date(row.original.created_at).toLocaleString(),
    enableResizing: true,
    enableSorting: true,
    size: 200,
  }),
  columnHelper.accessor('modified_at', {
    id: 'modified_at',
    cell: ({ row }) => new Date(row.original.modified_at).toLocaleString(),
    header: 'Updated At',
    enableResizing: true,
    enableSorting: true,
    size: 200,
  }),
  columnHelper.display({
    id: 'actions',
    cell: ({ row, table }) => {
      const { actions } = (table.options.meta ?? {}) as {
        actions: {
          handleDelete: (dataset: DatasetSchema) => void;
          deleteEnabled: boolean;
        };
      };

      if (!actions?.deleteEnabled) return null;

      return (
        <TableCellTextOverflow className="justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger
              onClick={(e) => e.stopPropagation()}
              className="flex h-6 w-6 items-center justify-center rounded-md transition-colors hover:bg-secondary focus:outline-none"
            >
              <EllipsisVerticalIcon className="h-5 w-5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="z-10"
              onClick={(e) => e.stopPropagation()}
            >
              <DropdownMenuItem
                onClick={() => actions.handleDelete(row.original)}
                className="flex items-center gap-2"
              >
                <TrashIcon className="h-5 w-5" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </TableCellTextOverflow>
      );
    },
    enableSorting: false,
    enableHiding: false,
    enableResizing: false,
    size: 40,
  }),
];

const EMPTY_TABLE = [];

const COLUMN_VISIBILITY_STATE_KEY = 'ls:ui:datasets-table-columns';

export const DatasetsTable = (props: TableProps) => {
  'use no memo';
  const { emptyState, simple } = props;

  const { authorize } = usePermissions();
  // navigation
  const navigate = useNavigate();

  // pagination
  const {
    paginationModel,
    setPaginationModel,
    resetPaginationModel,
    columnVisibilityModel,
    setColumnVisibilityModel,
  } = useDataGridState({
    columnVisibilityStateKey: COLUMN_VISIBILITY_STATE_KEY,
    defaultColumnVisibility: { data_type: false },
  });

  const { name, setName, debouncedName } =
    useDebouncedSearchStringQueryParams('search');

  const { selectedTags } = useStoredResourceTags();

  const [sortingState, setSortingState] = useSortingState(
    'last_session_start_time'
  );

  const mPaginationModel = simple
    ? props.defaultPaginationModel
    : paginationModel;

  const queryWithOutPagination = {
    name_contains: debouncedName ?? undefined,
    tag_value_id: selectedTags.map((tag) => tag.tag_value_id),
    sort_by: simple ? 'last_session_start_time' : sortingState[0].id,
    sort_by_desc: simple ? true : sortingState[0].desc,
  };

  useEffect(() => {
    if (!simple) {
      resetPaginationModel();
    }
    // Reset pagination when search term or tags change
  }, [debouncedName, selectedTags, simple, resetPaginationModel]);

  // data fetching
  const {
    data: rows,
    isLoading,
    isValidating,
    error,
  } = useDatasets({
    ...queryWithOutPagination,
    offset: mPaginationModel.pageIndex * mPaginationModel.pageSize,
    limit: mPaginationModel.pageSize,
    exclude_corrections_datasets: true,
  });

  // columns and actions
  const [deleteOpen, setDeleteOpen] = useState<DatasetSchema>();

  const [columnSizing, setColumnSizing] =
    useDataGridSizingLocalStorage('datasets');

  const table = useReactTable({
    columns: COLUMNS_DEF,
    data: rows ?? EMPTY_TABLE,
    state: {
      pagination: mPaginationModel,
      columnSizing,
      sorting: sortingState,
      columnVisibility: columnVisibilityModel,
    },
    meta: {
      actions: {
        handleDelete: setDeleteOpen,
        deleteEnabled: authorize('datasets:delete'),
      },
    },
    onPaginationChange: setPaginationModel,
    onColumnSizingChange: setColumnSizing,
    onColumnVisibilityChange: setColumnVisibilityModel,

    getRowId: (row) => row.id,

    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSortingState,

    manualExpanding: true,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,

    enableSorting: !simple,

    columnResizeMode: 'onChange',

    pageCount: Math.ceil(
      +(rows?.headers?.['x-pagination-total'] || 0) / mPaginationModel.pageSize
    ),
  });

  const organizationId = useOrganizationId();

  const renderContent = () => {
    if (isLoading && !rows?.length) {
      return <TableSkeleton rows={mPaginationModel.pageSize} rowHeight={56} />;
    }

    if (error) {
      return <ErrorBanner>{error.message}</ErrorBanner>;
    }

    if (
      !rows?.length &&
      !name &&
      emptyState &&
      selectedTags.length === 0 &&
      !isLoading
    ) {
      return emptyState;
    }

    return (
      <>
        <div className="min-h-[55px] overflow-hidden rounded-lg border border-secondary">
          <DataGrid
            table={table}
            cellHeight={56}
            cellSpacingVariant="wide"
            isLoading={isLoading || isValidating}
            emptyState={emptyState}
            onClick={(row, event) => {
              const target = `/${appOrganizationPath}/${organizationId}/${appDatasetsPath}/${row.id}`;
              if (!emulateNativeClick(target, event.nativeEvent)) {
                navigate(target);
              }
            }}
            onMiddleClick={(row) => {
              const target = `/${appOrganizationPath}/${organizationId}/${appDatasetsPath}/${row.id}`;
              if (!emulateNativeMiddleClick(target)) {
                navigate(target);
              }
            }}
            stickyRightColumn={true}
          />
        </div>

        {!simple && <DataGridPagination table={table} />}

        <DeleteModal
          endpoint={apiDatasetsPath}
          id={deleteOpen?.id}
          name={deleteOpen?.name}
          isOpen={!!deleteOpen}
          doClose={() => setDeleteOpen(undefined)}
        />
      </>
    );
  };

  return (
    <>
      {!simple && (
        <div className="mb-2 flex items-stretch justify-between">
          <div className="flex items-stretch gap-2">
            <DataGridSearchInput value={name} onChange={setName} />
          </div>
          <DataGridColumnVisibilityPopover
            table={table}
            localStorageKey={COLUMN_VISIBILITY_STATE_KEY}
          />
        </div>
      )}

      {renderContent()}
    </>
  );
};
